// Pure utilities for keyword extraction and experience-category classification.
// Used by the Analytics tab. Kept side-effect free so they can be unit tested.

export type CategoryKey = "staff" | "speed" | "quality" | "value" | "cleanliness";

export interface CategoryDef {
  key: CategoryKey;
  label: string;
  keywords: string[];
}

export const EXPERIENCE_CATEGORIES: CategoryDef[] = [
  {
    key: "staff",
    label: "Staff",
    keywords: [
      "staff", "team", "doctor", "nurse", "teacher", "therapist", "stylist",
      "receptionist", "friendly", "rude", "helpful", "attitude", "professional", "service",
    ],
  },
  {
    key: "speed",
    label: "Speed",
    keywords: ["wait", "waiting", "slow", "fast", "quick", "time", "late", "delayed", "response", "turnaround"],
  },
  {
    key: "quality",
    label: "Quality",
    keywords: ["food", "taste", "result", "quality", "skill", "technique", "treatment", "lesson", "cut", "colour", "flavor", "portion"],
  },
  {
    key: "value",
    label: "Value",
    keywords: ["price", "expensive", "cheap", "worth", "value", "cost", "overpriced", "affordable", "money"],
  },
  {
    key: "cleanliness",
    label: "Cleanliness",
    keywords: ["clean", "dirty", "hygiene", "neat", "messy", "smell", "tidy", "environment", "ambiance", "atmosphere"],
  },
];

export interface ReviewLite {
  comment_text?: string | null;
  sentiment?: string | null;
  rating?: number | null;
  outlet_id?: string | null;
  created_at?: string | null;
  posted_at?: string | null;
}

const tokenize = (text: string): string[] =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9\s']/g, " ")
    .split(/\s+/)
    .filter(Boolean);

export interface CategoryStats {
  key: CategoryKey;
  label: string;
  matched: number;
  positive: number;
  negative: number;
  neutral: number;
  positivePct: number;
  negativePct: number;
}

const inferSentiment = (r: ReviewLite): "positive" | "negative" | "neutral" => {
  const s = (r.sentiment || "").toLowerCase();
  if (s === "positive" || s === "negative" || s === "neutral") return s;
  const rating = typeof r.rating === "number" ? r.rating : Number(r.rating);
  if (Number.isFinite(rating)) {
    if (rating >= 4) return "positive";
    if (rating <= 2) return "negative";
    return "neutral";
  }
  return "neutral";
};

export const classifyReviewsByCategory = (reviews: ReviewLite[]): CategoryStats[] => {
  return EXPERIENCE_CATEGORIES.map((cat) => {
    const kwSet = new Set(cat.keywords);
    let matched = 0, positive = 0, negative = 0, neutral = 0;
    for (const r of reviews) {
      const text = (r.comment_text || "").toLowerCase();
      if (!text) continue;
      const tokens = tokenize(text);
      const hit = tokens.some((t) => kwSet.has(t));
      if (!hit) continue;
      matched += 1;
      const s = inferSentiment(r);
      if (s === "positive") positive += 1;
      else if (s === "negative") negative += 1;
      else neutral += 1;
    }
    const denom = matched || 1;
    return {
      key: cat.key,
      label: cat.label,
      matched,
      positive,
      negative,
      neutral,
      positivePct: matched ? Math.round((positive / denom) * 100) : 0,
      negativePct: matched ? Math.round((negative / denom) * 100) : 0,
    };
  });
};

const STOPWORDS = new Set([
  "the", "and", "for", "but", "with", "this", "that", "they", "have", "had", "has", "was", "were",
  "are", "you", "your", "our", "from", "not", "all", "any", "can", "just", "very", "out", "their",
  "them", "there", "then", "than", "what", "when", "where", "which", "who", "will", "would", "could",
  "should", "about", "into", "more", "some", "also", "been", "being", "because", "after", "before",
  "over", "under", "such", "only", "other", "much", "even", "still", "again", "ever", "now", "way",
  "get", "got", "one", "two", "three", "had", "did", "does", "doing", "done", "make", "made", "made",
  "like", "liked", "really", "good", "great", "nice", "bad", "best", "worst", "ok", "okay", "yes", "no",
  "too", "its", "it's", "i'm", "i've", "don't", "didn't", "wasn't", "weren't", "isn't", "aren't",
  "we", "us", "i", "a", "an", "is", "it", "to", "of", "in", "on", "at", "by", "be", "or", "as", "if",
  "so", "do", "my", "me", "he", "she", "his", "her", "him", "up", "down",
]);

export interface KeywordStat {
  word: string;
  total: number;
  positive: number;
  negative: number;
  neutral: number;
}

export const extractKeywordSentiment = (reviews: ReviewLite[], topN = 60): KeywordStat[] => {
  const map = new Map<string, KeywordStat>();
  for (const r of reviews) {
    const text = r.comment_text || "";
    if (!text) continue;
    const sentiment = inferSentiment(r);
    const seen = new Set<string>();
    for (const tok of tokenize(text)) {
      if (tok.length < 4) continue;
      if (STOPWORDS.has(tok)) continue;
      if (/^\d+$/.test(tok)) continue;
      if (seen.has(tok)) continue;
      seen.add(tok);
      const cur = map.get(tok) || { word: tok, total: 0, positive: 0, negative: 0, neutral: 0 };
      cur.total += 1;
      if (sentiment === "positive") cur.positive += 1;
      else if (sentiment === "negative") cur.negative += 1;
      else cur.neutral += 1;
      map.set(tok, cur);
    }
  }
  return Array.from(map.values()).sort((a, b) => b.total - a.total).slice(0, topN);
};

export interface DayBucket {
  dateISO: string; // YYYY-MM-DD
  total: number;
  positive: number;
  negative: number;
  neutral: number;
  positivePct: number; // 0-100, -1 if no reviews
}

export const bucketReviewsByDay = (reviews: ReviewLite[], days = 56): DayBucket[] => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const start = new Date(today);
  start.setDate(start.getDate() - (days - 1));

  const map = new Map<string, DayBucket>();
  for (let i = 0; i < days; i++) {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    const key = d.toISOString().slice(0, 10);
    map.set(key, { dateISO: key, total: 0, positive: 0, negative: 0, neutral: 0, positivePct: -1 });
  }
  for (const r of reviews) {
    const raw = r.posted_at || r.created_at;
    if (!raw) continue;
    const dt = new Date(raw);
    if (isNaN(dt.getTime())) continue;
    dt.setHours(0, 0, 0, 0);
    const key = dt.toISOString().slice(0, 10);
    const b = map.get(key);
    if (!b) continue;
    b.total += 1;
    const s = inferSentiment(r);
    if (s === "positive") b.positive += 1;
    else if (s === "negative") b.negative += 1;
    else b.neutral += 1;
  }
  for (const b of map.values()) {
    b.positivePct = b.total > 0 ? Math.round((b.positive / b.total) * 100) : -1;
  }
  return Array.from(map.values()).sort((a, b) => a.dateISO.localeCompare(b.dateISO));
};
